import { useAuth } from "@/src/contexts/auth.context";
import { Post } from "@/src/models/post.model";
import {
  ensureUserProfile,
  useGetUserProfileQuery,
  useToggleSavePostMutation,
} from "@/src/services/profile/profile.hooks";

export const useToggleSavePost = (post: Post | null) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const postId = post?.ItemId ?? "";
  const { data: userProfile } = useGetUserProfileQuery(userId);
  const { mutate: toggleSave } = useToggleSavePostMutation();

  const isSaved = userProfile?.savedPostIds?.includes(postId) ?? false;

  const handleToggleSave = async () => {
    if (!post || !userId) return;

    // Previously this returned early when the profile query had not resolved or
    // the user had no profile row yet — so the tap did nothing at all, with no
    // error and no visual change. Resolve (or create) the row instead, and read
    // the saved ids from that fresh copy rather than from a cache that may not
    // have loaded yet.
    const profile = userProfile ?? (await ensureUserProfile(userId));
    if (!profile) return;

    const currentSavedIds = profile.savedPostIds ?? [];
    const alreadySaved = currentSavedIds.includes(postId);

    const updatedSavedPostIds = alreadySaved
      ? currentSavedIds.filter((savedId) => savedId !== postId)
      : [...currentSavedIds, postId];

    toggleSave({
      userProfileId: profile.ItemId,
      savedPostIds: updatedSavedPostIds,
      postId,
      postSaveCount: post.saveCount ?? 0,
    });
  };

  return { isSaved, handleToggleSave };
};
